//
//  Signal.tsx
//  TrajeXY Signet Server
//
//  Copyright © 2026 TrajeXY. All rights reserved.
//
//  Created: October 24, 2026
//  Author: Bob Keathley
//
//  Off-Course Signal detail (§6u): problems, links to the run and captures,
//  status history (oldest first) and triage controls for team-admin and above.
//
import { useEffect, useState, useSyncExternalStore } from 'react'
import { api, getAssumed, subscribeAssumed } from '@shared/api/client'
import { isOk } from '@shared/api/envelope'
import { SIGNAL_STATUSES, actorLabel, canTriageSignals, signalErrorText, type SignalDetail, type SignalHistoryRow, type SignalProblem, type SignalStatus, type SignalTriage } from '@shared/api/signals'
import type { Session } from '@shared/auth'
import { href } from '@shared/router'

const when = (s: number) => new Date(s * 1000).toLocaleString()

export function Problems({ problems }: { problems: SignalProblem[] }) {
  return (
    <ol>
      {problems.map((p, i) => (
        <li key={i} className="problem">
          <div><b>What happened:</b> {p.what}</div>
          <div><span className="arrow">→</span> <b>The correction:</b> {p.correction}</div>
          <div className="hint">→ corrected by {p.corrected_by}</div>
        </li>
      ))}
    </ol>
  )
}

export function History({ rows }: { rows: SignalHistoryRow[] }) {
  return (
    <table>
      <thead><tr><th>When</th><th>Status</th><th>Note</th><th>Linked clause</th><th>By</th></tr></thead>
      <tbody>
        {rows.map((h, i) => (
          <tr key={i}>
            <td className="hint">{when(h.at)}</td>
            <td><span className={`pill st-${h.status}`}>{h.status}</span></td>
            <td>{h.note ?? '—'}</td>
            <td className="mono">{h.linked_clause ?? '—'}</td>
            <td className="hint">{actorLabel(h.by)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}

function Triage({ session, sig, onSaved }: { session: Session; sig: SignalDetail; onSaved: (s: SignalDetail) => void }) {
  const assumed = useSyncExternalStore(subscribeAssumed, getAssumed, getAssumed)
  const readOnly = assumed?.mode === 'read'
  const [status, setStatus] = useState<SignalStatus>(sig.status)
  const [note, setNote] = useState(sig.triage_note ?? '')
  const [clause, setClause] = useState(sig.linked_clause ?? '')
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)

  async function save() {
    setErr(null)
    const body: SignalTriage = {}
    if (status !== sig.status) body.status = status
    if (note !== (sig.triage_note ?? '')) body.triage_note = note === '' ? null : note
    if (clause !== (sig.linked_clause ?? '')) body.linked_clause = clause === '' ? null : clause
    if (Object.keys(body).length === 0) { setErr('Nothing changed.'); return }
    setBusy(true)
    const r = await api<SignalDetail>(`/signals/${sig.id}`, { method: 'PATCH', body: JSON.stringify(body) }, session.token)
    setBusy(false)
    if (!isOk(r)) { setErr(signalErrorText(r.error)); return }
    onSaved(r.data)
  }

  return (
    <div className="panel">
      <h3>Triage</h3>
      <label htmlFor="tr-status">Status</label>
      <select id="tr-status" value={status} onChange={(e) => setStatus(e.target.value as SignalStatus)}>
        {SIGNAL_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
      </select>
      <label htmlFor="tr-note">Triage note</label>
      <textarea id="tr-note" value={note} maxLength={2000} onChange={(e) => setNote(e.target.value)} />
      <label htmlFor="tr-clause">Linked clause (free text, e.g. canon/never-force-push)</label>
      <input id="tr-clause" value={clause} maxLength={500} onChange={(e) => setClause(e.target.value)} />
      {readOnly && <p className="hint">This is an assumed read-only session; it cannot triage.</p>}
      {err && <p className="err" role="alert">{err}</p>}
      <p><button className="btn" disabled={busy || readOnly} onClick={save}>{busy ? 'Saving…' : 'Save triage'}</button></p>
    </div>
  )
}

export default function Signal({ session, signalId }: { session: Session; signalId: string }) {
  const [sig, setSig] = useState<SignalDetail | null>(null)
  const [err, setErr] = useState<string | null>(null)

  useEffect(() => {
    setSig(null)
    setErr(null)
    api<SignalDetail>(`/signals/${encodeURIComponent(signalId)}`, {}, session.token).then((r) => {
      if (isOk(r)) setSig(r.data); else setErr(signalErrorText(r.error))
    })
  }, [signalId, session.token])

  return (
    <div className="signals">
      <p><a href={href('/signals')}>‹ Signals</a></p>
      {err && <p className="err" role="alert">{err}</p>}
      {sig === null && !err && <p className="hint">Loading signal…</p>}
      {sig && (
        <>
          <h2><span className="mono">{sig.id}</span> <span className={`pill st-${sig.status}`}>{sig.status}</span></h2>
          <p>{sig.summary}</p>
          <p className="hint">
            {sig.project_name} · agent <span className="mono">{sig.agent}</span> · {sig.source} · {when(sig.created_at)} · {actorLabel(sig.created_by)}
          </p>
          <p>
            <a href={href(`/runs/${sig.session_id}`)}>Open the run</a>
            {sig.deed_refs.map((d) => <span key={`${d.notebook_id}:${d.seq}`}> · <a href={href(`/notebooks/${d.notebook_id}/deeds`)}>deed {d.seq}</a></span>)}
          </p>
          {sig.capture_refs.length > 0 && (
            <>
              <h3>Captures</h3>
              <p className="mono">{sig.capture_refs.map((c) => <a key={c} href={href(`/runs/${sig.session_id}`)} title="Captures are in the run's timeline" style={{ marginRight: 12 }}>{c}</a>)}</p>
            </>
          )}
          <h3>Problems</h3>
          <Problems problems={sig.problems} />
          {(sig.triage_note || sig.linked_clause) && (
            <p className="hint">{sig.triage_note && <>Triage note: {sig.triage_note}. </>}{sig.linked_clause && <>Linked clause: <span className="mono">{sig.linked_clause}</span></>}</p>
          )}
          {canTriageSignals(session.user.role) && <Triage key={sig.history.length} session={session} sig={sig} onSaved={setSig} />}
          <h3>Status history</h3>
          <History rows={sig.history} />
        </>
      )}
    </div>
  )
}
